import { ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import { useGetMe } from "@workspace/api-client-react";
import { UserMenu } from "./user-menu";
import { WhatsNewButton } from "./whats-new";
import { RecommendationsBell } from "./recommendations-bell";
import { CommandPalette } from "./command-palette";
import { Activity, Check, ChevronsUpDown, Eye, EyeOff, Mail, Menu, Settings } from "lucide-react";
import {
  ALL_PULLERS,
  usePullerFilter,
  usePullerScope,
} from "@/contexts/puller-filter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const { isAll, workspaceCount, accountsCount } = usePullerScope();
  const { data: me } = useGetMe();
  const isAdmin = me?.role === "admin";
  const canManageSettings = me?.role === "admin" || me?.role === "operator";
  const canPrice = me?.role === "admin" || me?.role === "pricing";

  const navItems = [
    { href: "/", label: "Workspace", badge: isAll ? null : workspaceCount },
    { href: "/accounts", label: "Accounts", badge: isAll ? null : accountsCount },
    ...(canPrice ? [{ href: "/pricing", label: "Pricing", badge: null }] : []),
    { href: "/banking", label: "Banking", badge: null },
    { href: "/sales", label: "Sales", badge: null },
    ...(canPrice ? [{ href: "/skybox", label: "Skybox", badge: null }] : []),
    ...(isAdmin ? [{ href: "/ticketransfer", label: "Ticketransfer", badge: null }] : []),
  ];

  return (
    <div className="h-screen w-full bg-background text-foreground flex flex-col font-sans selection:bg-primary/30 overflow-x-hidden">
      <header className="h-12 border-b border-border bg-card flex items-center justify-between gap-3 px-3 sm:px-4 shrink-0 shadow-sm z-10">
        <div className="flex items-center gap-4 lg:gap-6 min-w-0">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer opacity-90 hover:opacity-100 transition-opacity">
              <img src="/logo.svg" alt="TEX FVE Handler" className="w-5 h-5" />
              <span className="font-semibold text-sm tracking-tight text-foreground">TEX FVE</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.href} href={item.href}>
                <div
                  className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-colors flex items-center gap-1.5 ${
                    location === item.href || (item.href !== "/" && location.startsWith(item.href))
                      ? "bg-secondary text-foreground"
                      : "text-secondary-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  {item.label}
                  {item.badge !== null && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary/20 text-primary text-[10px] font-mono font-semibold leading-none">
                      {item.badge}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </nav>
        </div>

        <div className="hidden lg:flex items-center gap-4 shrink-0">
          <ShowBadPhonesToggle />
          <PullerHeaderSelect />
          <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded border border-border bg-muted/50 text-[10px] text-muted-foreground font-mono">
            <span>Cmd</span>
            <span>K</span>
          </div>
          <WhatsNewButton />
          <RecommendationsBell />
          {isAdmin && (
            <Link href="/activity" title="Activity" aria-label="Activity">
              <div
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md cursor-pointer transition-colors",
                  location === "/activity" || location.startsWith("/activity")
                    ? "bg-secondary text-foreground"
                    : "text-secondary-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Activity className="h-4 w-4" />
              </div>
            </Link>
          )}
          <Link href="/emails" title="Emails" aria-label="Emails">
            <div
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md cursor-pointer transition-colors",
                location === "/emails" || location.startsWith("/emails")
                  ? "bg-secondary text-foreground"
                  : "text-secondary-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Mail className="h-4 w-4" />
            </div>
          </Link>
          {canManageSettings && (
            <Link href="/settings" title="Settings" aria-label="Settings">
              <div
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md cursor-pointer transition-colors",
                  location === "/settings" || location.startsWith("/settings")
                    ? "bg-secondary text-foreground"
                    : "text-secondary-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Settings className="h-4 w-4" />
              </div>
            </Link>
          )}
          <UserMenu />
        </div>

        <div className="flex lg:hidden items-center gap-1.5 shrink-0">
          <RecommendationsBell />
          <UserMenu />
          <MobileMenu
            navItems={navItems}
            location={location}
            isAdmin={isAdmin}
            canManageSettings={canManageSettings}
          />
        </div>
      </header>

      <main className="flex-1 flex overflow-hidden">
        {children}
      </main>

      <CommandPalette />
    </div>
  );
}

interface MobileMenuProps {
  navItems: { href: string; label: string; badge: number | null }[];
  location: string;
  isAdmin: boolean;
  canManageSettings: boolean;
}

function MobileMenu({ navItems, location, isAdmin, canManageSettings }: MobileMenuProps) {
  const [open, setOpen] = useState(false);

  const isActive = (href: string) =>
    location === href || (href !== "/" && location.startsWith(href));

  const iconItems = [
    ...(isAdmin ? [{ href: "/activity", label: "Activity", icon: Activity }] : []),
    { href: "/emails", label: "Emails", icon: Mail },
    ...(canManageSettings ? [{ href: "/settings", label: "Settings", icon: Settings }] : []),
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Open menu"
          className="flex h-8 w-8 items-center justify-center rounded-md text-secondary-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-72 p-0 flex flex-col">
        <SheetHeader className="px-4 py-3 border-b border-border text-left">
          <SheetTitle className="text-sm">Menu</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link key={item.href} href={item.href}>
                <div
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors",
                    isActive(item.href)
                      ? "bg-secondary text-foreground"
                      : "text-secondary-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <span>{item.label}</span>
                  {item.badge !== null && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary/20 text-primary text-[10px] font-mono font-semibold leading-none">
                      {item.badge}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </nav>

          <div className="border-t border-border" />

          <nav className="space-y-1">
            {iconItems.map((item) => (
              <Link key={item.href} href={item.href}>
                <div
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors",
                    isActive(item.href)
                      ? "bg-secondary text-foreground"
                      : "text-secondary-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                </div>
              </Link>
            ))}
          </nav>

          <div className="border-t border-border" />

          <div className="space-y-3 px-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Bad phones</span>
              <ShowBadPhonesToggle />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Puller</span>
              <PullerHeaderSelect />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">What's new</span>
              <WhatsNewButton />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ShowBadPhonesToggle() {
  const { showBadPhones, setShowBadPhones } = usePullerFilter();
  // Intentionally a native `title` hint rather than a Radix Tooltip: in the
  // production (React 19) build this header control was intermittently tripping
  // minified React #185 ("Maximum update depth exceeded") from the Radix
  // Tooltip/Popper stack, crashing the whole app into the error boundary. A
  // native title carries the same hover hint with none of the popper machinery.
  return (
    <button
      type="button"
      role="switch"
      aria-checked={showBadPhones}
      aria-label="Show bad phones"
      title={
        showBadPhones
          ? "Showing bad-phone inventory — click to hide"
          : "Bad-phone inventory hidden — click to show"
      }
      onClick={() => setShowBadPhones(!showBadPhones)}
      className={cn(
        "h-7 inline-flex items-center gap-1.5 rounded-md border px-2 text-[11px] font-medium transition-colors",
        showBadPhones
          ? "border-primary/40 bg-primary/15 text-primary"
          : "border-input bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {showBadPhones ? (
        <Eye className="h-3.5 w-3.5" />
      ) : (
        <EyeOff className="h-3.5 w-3.5" />
      )}
      <span className="hidden sm:inline">Bad phones</span>
    </button>
  );
}

function PullerHeaderSelect() {
  const { selectValue, setPuller, pullers, pullerName } = usePullerFilter();
  const [open, setOpen] = useState(false);

  const isAll = selectValue === ALL_PULLERS;
  const label = isAll ? "All Pullers" : pullerName ?? "All Pullers";

  // Total remaining listings across all active pullers. Dedupe by tag because
  // multiple puller records can share a tag and carry the same per-tag count.
  const totalListings = (() => {
    const byTag = new Map<string, number>();
    let any = false;
    for (const p of pullers) {
      if (p.listingCount == null) continue;
      any = true;
      if (p.tag != null) byTag.set(p.tag, p.listingCount);
      else byTag.set(`__id_${p.id}`, p.listingCount);
    }
    if (!any) return null;
    return [...byTag.values()].reduce((sum, n) => sum + n, 0);
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Puller filter"
          className="h-7 w-40 text-xs bg-muted/50 inline-flex items-center justify-between gap-1 rounded-md border border-input px-3 py-1 text-foreground shadow-sm transition-colors hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="end">
        <Command>
          <CommandInput placeholder="Search pullers..." className="h-8 text-xs" />
          <CommandList className="max-h-64">
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
              No pullers found.
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="All Pullers"
                className="text-xs"
                onSelect={() => {
                  setPuller(ALL_PULLERS);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-3.5 w-3.5",
                    isAll ? "opacity-100" : "opacity-0",
                  )}
                />
                <span>All Pullers</span>
                {totalListings != null && (
                  <span className="ml-auto pl-2 shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
                    {totalListings}
                  </span>
                )}
              </CommandItem>
              {pullers.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.name}
                  className="text-xs"
                  onSelect={() => {
                    setPuller(p.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5",
                      selectValue === String(p.id) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{p.name}</span>
                  {p.listingCount != null && (
                    <span className="ml-auto pl-2 shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
                      {p.listingCount}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
